import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { InputField } from 'Components/reusable/InputFields/InputField'
import { Context } from '../context'
import { AnyObject, ContextValue, EditableFieldsOfType, getFieldControlId } from '../types'

/**
 * Password field.
 */
export const DataEditContextPasswordField = <T extends AnyObject>({
  field,
  showPassowordOrHide,
  insetLabel,
  ...rest
}: { field: EditableFieldsOfType<T, string>; showPassowordOrHide?: boolean; insetLabel?: boolean } & Omit<
  Record<string, unknown>,
  'onChange' | 'value' | 'error' | 'name'
>) => {
  const { flatData, errors, disabled, setFields, handleSubmit, localeBase, t } = useContext<ContextValue<T>>(Context)
  const controlId = getFieldControlId(field, rest.id as string | undefined, rest.testId as string | undefined)

  const [value, setValue] = useState<string>(flatData[field] as unknown as string)
  const [showPassword, setShowPassword] = useState<boolean>(false)

  const error = errors[field]

  const setField = useMemo(() => (newValue: string) => setFields({ [field]: newValue }), [setFields, field])

  useEffect(() => {
    setValue(flatData[field] as unknown as string)
  }, [field, flatData])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    setValue(newValue)
    setField(newValue)
  }

  const handleShowPassword = () => {
    setShowPassword(!showPassword)
  }

  return (
    <InputField
      id={controlId}
      onKeyDown={e => handleSubmit(e)}
      onChange={handleChange}
      value={value ?? ''}
      error={error}
      disabled={disabled[field]}
      name={field}
      label={t(`${localeBase}.${field}`)}
      type={showPassword ? 'text' : 'password'}
      insetLabel={insetLabel}
      endAdornment={
        <>
          {showPassowordOrHide && (
            <InputAdornment position="end">
              <IconButton
                data-testid={`button-toggle-password-${controlId}`}
                aria-label="toggle password visibility"
                onClick={handleShowPassword}
                edge="end"
                size="large"
              >
                {showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
              </IconButton>
            </InputAdornment>
          )}
        </>
      }
      {...rest}
    />
  )
}

/**
 * Text field.
 */
